import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DocumentService } from '../../../_services/document.service';

interface FileUploadState {
    file: File | null;
    uploading: boolean;
    uploaded: boolean;
    error: string | null;
    fileName: string;
    fileSize: number;
}

@Component({
    selector: 'app-document-upload-modal',
    templateUrl: './document-upload-modal.component.html',
    styleUrls: ['./document-upload-modal.component.css'],
    standalone: false
})
export class DocumentUploadModalComponent implements OnInit {
    @Input() isOpen = false;
    @Input() documentType: 'registration' | 'identity' = 'registration';
    @Input() sellerType: 'private' | 'company' = 'company';
    @Input() sellerId: string = '';
    @Input() autoReload = false; // If true, reloads page after successful upload
    @Input() resourceType: 'PrivateSeller' | 'CompanySeller' | 'LogisticAgent' | null = null; // Optional override for resource type
    @Output() close = new EventEmitter<void>();
    @Output() uploadComplete = new EventEmitter<string>(); // Emits the document path

    // ID document selection (for identity document only)
    selectedIdDocType: 'IdCard' | 'Passport' | null = null;

    // Upload state
    uploadState: FileUploadState = {
        file: null,
        uploading: false,
        uploaded: false,
        error: null,
        fileName: '',
        fileSize: 0
    };

    // Constants
    readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    readonly ACCEPTED_TYPES = 'image/png,image/jpeg,image/jpg,application/pdf';

    constructor(
        private translateService: TranslateService,
        private toastr: ToastrService,
        private documentService: DocumentService
    ) {}

    ngOnInit(): void {
        this.resetUpload();
    }

    get isRegistration(): boolean {
        return this.documentType === 'registration';
    }

    get isIdentity(): boolean {
        return this.documentType === 'identity';
    }

    get canUpload(): boolean {
        if (this.isIdentity) {
            return this.selectedIdDocType !== null && 
                   this.uploadState.file !== null && 
                   !this.uploadState.uploading && 
                   !this.uploadState.uploaded;
        }
        return this.uploadState.file !== null && 
               !this.uploadState.uploading && 
               !this.uploadState.uploaded;
    }

    get modalTitle(): string {
        if (this.isRegistration) {
            return this.translateService.instant('seller_profile.upload_registration_title');
        }
        return this.translateService.instant('seller_profile.upload_identity_title');
    }

    onIdDocTypeChange(type: 'IdCard' | 'Passport'): void {
        this.selectedIdDocType = type;
        this.resetFile();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (this.validateFile(file)) {
                this.uploadState.file = file;
                this.uploadState.fileName = file.name;
                this.uploadState.fileSize = file.size;
                this.uploadState.error = null;
            }
        }
    }

    validateFile(file: File): boolean {
        // Check file size
        if (file.size > this.MAX_FILE_SIZE) {
            this.uploadState.error = 'File size exceeds 5MB limit';
            this.translateService.get('seller_setup.error_file_too_large').subscribe(msg => {
                this.toastr.error(msg || 'File size exceeds 5MB limit');
            });
            return false;
        }

        // Check file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            this.uploadState.error = 'Invalid file type. Only PNG, JPG, JPEG, and PDF are allowed';
            this.translateService.get('seller_setup.error_invalid_file_type').subscribe(msg => {
                this.toastr.error(msg || 'Invalid file type. Only PNG, JPG, JPEG, and PDF are allowed');
            });
            return false;
        }

        return true;
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    uploadDocument(): void {
        if (!this.uploadState.file) {
            return;
        }

        this.uploadState.uploading = true;
        this.uploadState.error = null;

        const docType = this.isRegistration ? 'RegistrationDoc' : this.selectedIdDocType!;
        // Use explicit resourceType if provided, otherwise derive from sellerType
        const resource = this.resourceType || (this.sellerType === 'private' ? 'PrivateSeller' : 'CompanySeller');

        console.log('[UPLOAD-MODAL] Starting upload for seller:', this.sellerId);
        console.log('[UPLOAD-MODAL] sellerType input:', this.sellerType);
        console.log('[UPLOAD-MODAL] resourceType input:', this.resourceType);
        console.log('[UPLOAD-MODAL] Computed resource:', resource);
        console.log('[UPLOAD-MODAL] documentType:', docType);
        console.log('[UPLOAD-MODAL] sellerId:', this.sellerId);
        
        this.documentService.uploadDocument(
            this.uploadState.file,
            this.sellerId,
            resource,
            docType as any
        ).subscribe({
            next: (response: any) => {
                console.log('MODAL: Upload success, response:', response);
                this.uploadState.uploading = false;
                this.uploadState.uploaded = true;
                this.translateService.get('seller_setup.success_upload').subscribe(msg => {
                    this.toastr.success(msg || 'Document uploaded successfully');
                });
                
                // Emit the document path from response
                const docPath = response?.data?.docPath || response?.data?.path || response?.path;
                console.log('MODAL: Extracted docPath:', docPath);
                
                if (this.autoReload) {
                    // Auto reload page after successful upload (for dashboard)
                    console.log('[UPLOAD-MODAL] Auto reload enabled, reloading page in 1.5s...');
                    console.log('[UPLOAD-MODAL] Response data:', response?.data);
                    setTimeout(() => {
                        console.log('[UPLOAD-MODAL] Reloading page now');
                        window.location.reload();
                    }, 1500); // Slightly longer delay to show success message
                } else if (docPath) {
                    // Normal flow: emit event with docPath (for setup page)
                    console.log('MODAL: Emitting uploadComplete with docPath');
                    setTimeout(() => {
                        this.uploadComplete.emit(docPath);
                        console.log('MODAL: uploadComplete emitted, closing modal');
                        this.onClose();
                    }, 1000); // Small delay to show success message
                } else {
                    console.log('MODAL: No docPath found in response!');
                    setTimeout(() => {
                        this.onClose();
                    }, 1000);
                }
            },
            error: (error) => {
                console.log('MODAL: Upload error:', error);
                this.uploadState.uploading = false;
                this.uploadState.error = error?.error?.message || 'Upload failed. Please try again.';
                this.translateService.get('seller_setup.error_upload').subscribe(msg => {
                    this.toastr.error(msg || 'Failed to upload document');
                });
            }
        });
    }

    resetFile(): void {
        this.uploadState.file = null;
        this.uploadState.fileName = '';
        this.uploadState.fileSize = 0;
        this.uploadState.error = null;
    }

    resetUpload(): void {
        this.selectedIdDocType = null;
        this.uploadState = {
            file: null,
            uploading: false,
            uploaded: false,
            error: null,
            fileName: '',
            fileSize: 0
        };
    }

    clearFile(): void {
        this.resetFile();
    }

    retryUpload(): void {
        this.uploadState.error = null;
    }

    onClose(): void {
        this.resetUpload();
        this.close.emit();
    }
}
